import { useState, useLayoutEffect, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2, Mail, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.png";
import PasswordStrengthIndicator from "@/components/PasswordStrengthIndicator";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  
  // Store tokens in refs IMMEDIATELY - these persist even if hash is cleared
  const accessTokenRef = useRef<string | null>(null);
  const refreshTokenRef = useRef<string | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [checkComplete, setCheckComplete] = useState(false);

  // Check for existing session ONLY if there's recovery evidence
  useEffect(() => {
    const hasRecoveryEvidence = 
      window.location.hash.includes('type=recovery') ||
      window.location.href.includes('type=recovery') ||
      sessionStorage.getItem('password_recovery_pending') === 'true';

    if (!hasRecoveryEvidence) {
      // No recovery evidence — this is NOT a password reset flow.
      // Could be an OAuth redirect or direct navigation. Don't authorize.
      console.log("🔐 No recovery evidence on /reset-password — skipping auth");
      setCheckComplete(true);
      return;
    }

    const checkExistingSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        console.log("🔐 Recovery session found — showing update form");
        setIsAuthorized(true);
        accessTokenRef.current = session.access_token;
        refreshTokenRef.current = session.refresh_token;
      }
      setCheckComplete(true);
    };
    checkExistingSession();

    // Code-based reset links sign in a moment after the page opens
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === "PASSWORD_RECOVERY" || event === "SIGNED_IN") && session?.user) {
        accessTokenRef.current = session.access_token;
        refreshTokenRef.current = session.refresh_token;
        setIsAuthorized(true);
        setCheckComplete(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // useLayoutEffect runs SYNCHRONOUSLY before paint - captures tokens before anything else
  useLayoutEffect(() => {
    // Skip if already authorized from existing session
    if (isAuthorized) return;
    
    const hash = window.location.hash;
    const href = window.location.href;
    
    console.log("🔐 useLayoutEffect - Capturing tokens immediately");
    console.log("Current Hash:", hash);
    console.log("Full URL:", href);
    
    // Parse tokens from hash
    if (hash) {
      const hashParams = new URLSearchParams(hash.substring(1));
      const accessToken = hashParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token');
      
      console.log("🔐 Parsed from hash:", { 
        hasAccessToken: !!accessToken, 
        hasRefreshToken: !!refreshToken 
      });
      
      if (accessToken && refreshToken) {
        // Store in refs immediately - these won't be affected by re-renders
        accessTokenRef.current = accessToken;
        refreshTokenRef.current = refreshToken;
        setIsAuthorized(true);
        console.log("🔐 Tokens captured and stored in refs");
      }
    }
    
    // Also check sessionStorage backup from main.tsx
    const storedHash = sessionStorage.getItem('password_recovery_hash');
    if (!accessTokenRef.current && storedHash) {
      console.log("🔐 Restoring tokens from sessionStorage backup");
      const hashParams = new URLSearchParams(storedHash.substring(1));
      accessTokenRef.current = hashParams.get('access_token');
      refreshTokenRef.current = hashParams.get('refresh_token');
      if (accessTokenRef.current && refreshTokenRef.current) {
        setIsAuthorized(true);
      }
    }
    
    setCheckComplete(true);
    
    // Clear the sessionStorage flag now that we've captured the tokens
    sessionStorage.removeItem('password_recovery_pending');
  }, [isAuthorized]);

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      // Get tokens from refs (captured in useLayoutEffect)
      const accessToken = accessTokenRef.current;
      const refreshToken = refreshTokenRef.current;
      
      console.log("🔐 Using tokens from refs:", { 
        hasAccessToken: !!accessToken, 
        hasRefreshToken: !!refreshToken 
      });

      if (!accessToken || !refreshToken) {
        toast.error("Invalid reset link. Please request a new one.");
        setIsAuthorized(false);
        return;
      }

      // Step 1: Force set the session using stored tokens
      console.log("🔐 Setting session from stored tokens...");
      const { error: sessionError } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken,
      });

      if (sessionError) {
        console.error("🔐 Session error:", sessionError.message);
        toast.error("Your reset link has expired. Please request a new one.");
        setIsAuthorized(false);
        return;
      }
      console.log("🔐 Session set successfully!");

      // Step 2: Update password immediately
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        console.error("🔐 Update error:", updateError.message);
        toast.error(updateError.message);
        return;
      }

      // Step 3: Success - clean up completely
      console.log("🔐 Password updated successfully!");
      
      // Clear refs immediately
      accessTokenRef.current = null;
      refreshTokenRef.current = null;
      
      // Clear ALL recovery flags from sessionStorage
      sessionStorage.removeItem('password_recovery_hash');
      sessionStorage.removeItem('password_recovery_pending');
      
      // CRITICAL: Sign out IMMEDIATELY to kill the recovery session
      await supabase.auth.signOut();
      
      // CRITICAL: Permanently clear URL - use origin + path to remove ALL hash fragments
      window.history.replaceState(null, '', window.location.origin + '/login');
      
      toast.success("Password updated successfully! Please sign in with your new password.");

      // Navigate to login (URL already updated, this just triggers React Router)
      navigate("/login", { replace: true });
    } catch (error: any) {
      console.error("🔐 Unexpected error:", error);
      toast.error(error?.message || "Failed to update password. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendLink = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setResendLoading(true);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password?type=recovery`,
      });

      if (error) {
        toast.error(error.message);
        return;
      }

      toast.success("Reset link sent! Check your email inbox.");
      setEmail("");
    } catch (error) {
      toast.error("Failed to send reset link");
    } finally {
      setResendLoading(false);
    }
  };

  // Show loading while checking authorization
  if (!checkComplete) {
    return (
      <div className="min-h-screen bg-background relative overflow-hidden flex items-center justify-center">
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            background: "linear-gradient(180deg, hsl(0 0% 10%) 0%, hsl(0 0% 5%) 100%)",
          }}
        />
        <div className="relative z-10 flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-cyan" />
          <p className="text-muted-foreground">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Gradient background */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: "linear-gradient(180deg, hsl(0 0% 10%) 0%, hsl(0 0% 5%) 100%)",
        }}
      />

      {/* Subtle cyan glow at top */}
      <div
        className="fixed top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] pointer-events-none opacity-15"
        style={{
          background: "radial-gradient(ellipse at center, hsl(184 100% 50% / 0.4) 0%, transparent 70%)",
        }}
      />

      <div className="relative z-10 container mx-auto px-4 py-12 max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src={logo}
            alt="GreyRock AI"
            className="w-20 h-20 object-contain drop-shadow-[0_0_25px_rgba(0,242,255,0.3)]"
          />
        </div>

        {/* Card */}
        <div className="glass-card p-6">
          <h1 className="text-2xl font-bold text-foreground text-center mb-2">
            {isAuthorized ? "Set New Password" : "Reset Your Password"}
          </h1>
          <p className="text-sm text-muted-foreground text-center mb-6">
            {isAuthorized
              ? "Enter your new password below."
              : "Enter your email to receive a new reset link."}
          </p>

          {isAuthorized ? (
            // Password update form - shown when user clicked a valid reset link
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-foreground">
                  New Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="new-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={6}
                    className="pl-10 pr-10 bg-charcoal-medium border-glass-border text-foreground"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <PasswordStrengthIndicator password={newPassword} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-foreground">
                  Confirm Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirm-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength={6}
                    className="pl-10 pr-10 bg-charcoal-medium border-glass-border text-foreground"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className={cn(
                  "w-full py-3 rounded-xl font-bold",
                  "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                  "hover:opacity-90 transition-opacity"
                )}
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  "Update Password"
                )}
              </Button>
            </form>
          ) : (
            // Resend link form - shown when no valid recovery token
            <div className="space-y-6">
              <div className="p-4 rounded-lg bg-charcoal-medium/50 border border-glass-border">
                <p className="text-muted-foreground text-sm text-center">
                  Your reset link may have expired or is invalid.
                </p>
              </div>

              <form onSubmit={handleResendLink} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-foreground">
                    Email Address
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="pl-10 bg-charcoal-medium border-glass-border text-foreground"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={resendLoading}
                  className={cn(
                    "w-full py-3 rounded-xl font-bold",
                    "bg-gradient-to-r from-cyan to-cyan-glow text-primary-foreground",
                    "hover:opacity-90 transition-opacity"
                  )}
                >
                  {resendLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    "Send New Reset Link"
                  )}
                </Button>
              </form>

              <div className="text-center">
                <a
                  href="/"
                  className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                  ← Back to Home
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
